import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import favoritesService from '../services/favoritesService';

const POLL_INTERVAL_MS = 30_000;

interface FavoritesCountContextValue {
  count: number;
  refreshCount: () => void;
}

const FavoritesCountContext = createContext<FavoritesCountContextValue>({
  count: 0,
  refreshCount: () => {},
});

export function FavoritesCountProvider({ children }: { children: React.ReactNode }) {
  const [count, setCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCount = useCallback(async () => {
    try {
      const { count: total } = await favoritesService.getCount();
      setCount(total);
    } catch {
      // Silently ignore — badge will just show last known count
    }
  }, []);

  useEffect(() => {
    fetchCount();
    intervalRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchCount]);

  return (
    <FavoritesCountContext.Provider value={{ count, refreshCount: fetchCount }}>
      {children}
    </FavoritesCountContext.Provider>
  );
}

export function useFavoritesCountContext(): FavoritesCountContextValue {
  return useContext(FavoritesCountContext);
}
